import type { AlignedPair } from '../vocal/align-notes.js'

/**
 * The timing layer - pure.
 *
 * Pitch has already decided which note is which: the pairs come from `alignNotes`, DTW over the
 * note SEQUENCES rather than over clock time. This module measures when each paired note actually
 * happened, which is a different question from whether it was the right note and is answered with
 * a different set of numbers.
 *
 * Milliseconds in, verdicts out. No audio, no clock, no React - so every rule here is a number a
 * test can plant and read back, which is the only way a tool whose entire output is a measurement
 * of a musician can be trusted at all.
 *
 * **Every number here is RELATIVE to the phrase's own mean placement.** The absolute question -
 * "was that note on the beat?" - is not asked, because this codebase cannot currently answer it:
 * the reference the singer performs against goes out through `playScheduled` (220 ms of lookahead
 * plus the device's own output latency), hardware output latency is unreachable through
 * `standardized-audio-context`, and there is no calibration on the same path to subtract. What
 * survives all of that is placement WITHIN the take, because an uncompensated constant delay
 * shifts every onset equally and cancels in the centring below (Bowie, 2026-09-13).
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §6.2, §8
 */

/**
 * "Together with the rest".
 *
 * 25 ms is roughly the threshold below which two onsets fuse - past it a listener hears two
 * events, under it one. The ear calls this "together", so the tool does too, and the direction of
 * an error this small is not reported: it is not information the player can act on.
 * @see spec §6.2
 */
export const timingOnMs = 25

/**
 * "A touch early / late" against the player's own average. Audibly displaced, still in the pocket:
 * a listener hears the note lean without hearing it as being in the wrong place.
 * @see spec §6.2
 */
export const timingCloseMs = 60

/**
 * Past this, the note has moved to a different grid position rather than merely been late.
 *
 * At 120 BPM, 125 ms is a sixteenth: a note this far from the rest of the phrase is not sitting
 * behind it, it is sitting on a different beat. DTW pairs by PITCH, so a right note 300 ms out
 * still pairs and still earns its pitch score - what changes is the timing verdict, which becomes
 * displacement rather than lateness (Bowie, 2026-09-09).
 *
 * The consequence runs deeper than a label. A displaced note takes no part in the second centring
 * pass and none in the feel-match series, because neither question applies to it: both are about a
 * lean, and a lean is a small signed distance from where a note belongs. Letting a 400 ms
 * displacement set the phrase's centre turns one misplaced note into a verdict about the whole
 * take.
 * @see spec §6.2
 */
export const timingOffMs = 120

/**
 * The reference needs at least this much lean somewhere before a feel match is reported at all.
 *
 * Correlating a performance against a straight reference measures nothing: the reference series
 * is (near) constant, its variance is noise, and the resulting r would swing between +1 and -1 on
 * rounding. §6.2 gates the sentence on the melody having a non-trivial feel, and 20 ms is that
 * gate - about the fusion threshold, below which the "feel" is not audible as feel.
 * @see spec §6.2
 */
export const feelMatchMinOffsetMs = 20

export type TimingBand = 'on' | 'close' | 'off' | 'wrong-place'

export interface NoteTiming {
  /**
   * Signed ms from the PHRASE'S OWN MEAN placement. Negative is ahead of the player's own average.
   *
   * Not a distance from the beat, and the copy that renders it must not say it is: any constant
   * device delay is inside the mean this was measured from, and it is removed from every note
   * together.
   */
  errorMs: number
  band: TimingBand
}

export interface ScoreTimingInput {
  /** From `alignNotes` - pitch decides which note is which, then timing is measured on the pairs. */
  pairs: AlignedPair[]
  /** Performed onsets in ms from the take's beat 1, indexed as `pair.sungIndex`. */
  performedOnsetsMs: number[]
  /**
   * Reference onsets in ms from the melody's start, indexed as `pair.referenceIndex`.
   *
   * These are `scheduleMelody`'s timestamps, WITH the offsets - §6.1: the user trains against the
   * melody as it is, feel included. A reference flattened to its grid would score a performer for
   * reproducing exactly what they were played.
   */
  referenceOnsetsMs: number[]
  /** The reference notes' own `offsetMs`, index-aligned with `referenceOnsetsMs`. */
  referenceOffsetsMs: number[]
}

export interface TimingScore {
  perNote: NoteTiming[]
  /**
   * Correlation between the offsets the player produced and the offsets the melody carries.
   *
   * Invariant to a constant delay by construction, and was so before the centring existed:
   * Pearson's r subtracts each series' own mean, so shifting every performed onset by the same
   * amount moves `performedOffsetMs` by that amount and leaves r untouched. This is the one number
   * here that never needed the calibration.
   *
   * `undefined` - never 0 - when there is nothing to correlate: a straight reference, fewer than
   * two pairs, or a series with no variance. A zero would read as "you did not catch the feel",
   * which is a verdict, and there is no verdict to give.
   */
  feelMatch: number | undefined
}

const bandOf = (errorMs: number): TimingBand => {
  const distance = Math.abs(errorMs)
  if (distance <= timingOnMs) {
    return 'on'
  }
  if (distance <= timingCloseMs) {
    return 'close'
  }
  return distance <= timingOffMs ? 'off' : 'wrong-place'
}

const mean = (values: number[]) => values.reduce((total, value) => total + value, 0) / values.length

/**
 * Pearson's r between the offsets the player produced and the offsets the melody carries.
 *
 * The two zero-variance cases are NOT symmetric, and treating them as if they were is the bug
 * this comment exists to prevent:
 *
 *  - **The reference never varies** - every note leaning the same way, or not at all. There is no
 *    shape to follow, so there is no verdict: `undefined`. Returning a number here would report
 *    the sign of a rounding error as a musical finding.
 *  - **The performer never varies** - every note placed exactly on its grid line. This is the
 *    single most important case the feel match exists to name: it is `timing-feel-straight`, the
 *    player played it straight against a melody that leans. Their placement carries no
 *    information about the reference's shape, which is a correlation of exactly **0**, and
 *    `undefined` would silently delete the sentence in the one case it was written for.
 *
 * Fewer than two pairs is `undefined` for the same reason as a constant reference: two points
 * always correlate perfectly, and one does not correlate at all.
 */
const feelCorrelation = (performed: number[], reference: number[]): number | undefined => {
  if (performed.length < 2) {
    return undefined
  }
  const performedBar = mean(performed)
  const referenceBar = mean(reference)
  let covariance = 0
  let performedVariance = 0
  let referenceVariance = 0
  for (const [index, value] of performed.entries()) {
    const dp = value - performedBar
    const dr = reference[index] - referenceBar
    covariance += dp * dr
    performedVariance += dp * dp
    referenceVariance += dr * dr
  }
  if (referenceVariance === 0) {
    return undefined
  }
  if (performedVariance === 0) {
    return 0
  }
  return covariance / Math.sqrt(performedVariance * referenceVariance)
}

/**
 * Where the phrase as a whole sat, in raw ms - the number every note is then measured from.
 *
 * TWO PASSES, and the second one is what keeps a single misplaced note from rewriting the verdict
 * on the notes that agreed with each other. The first pass centres on every measured note and
 * bands the result, which is only used to find the displaced ones; the second re-centres on the
 * notes that were merely leaning. Three notes 10 ms late and one 400 ms out centre on 10 ms, not
 * on 107.5 ms - the latter would band the three that were together as `off`.
 *
 * Both passes are exactly equivariant under a constant delay - `mean(x + d) = mean(x) + d`, and
 * the second pass's membership is decided from first-pass errors that already have the constant
 * removed - so the centre absorbs an uncompensated device delay entirely and every reported number
 * is unchanged by it. That is the property the removed calibration was there to buy, and it is
 * asserted directly in `score-timing.test.ts`.
 *
 * Deliberately NOT returned to the caller. It is the raw offset of the whole phrase, which is the
 * player's lean and the device's delay added together and inseparable - the one number this
 * module must not report (Bowie, 2026-09-13).
 */
const phraseCentreMs = (rawErrorsMs: number[]): number => {
  const firstPass = mean(rawErrorsMs)
  const leaning = rawErrorsMs.map(error => error - firstPass).filter(error => bandOf(error) !== 'wrong-place')
  /* Every note displaced from the first-pass centre: there is no smaller set to re-centre on. */
  return leaning.length === 0 ? firstPass : firstPass + mean(leaning)
}

export const scoreTiming = (input: ScoreTimingInput): TimingScore | null => {
  const { pairs, performedOnsetsMs, referenceOnsetsMs, referenceOffsetsMs } = input

  /*
   * A pair naming an index outside either array is dropped rather than allowed to produce NaN. It
   * means the caller aligned against a different note list from the one it passed here, and a NaN
   * would travel all the way to a sentence about the player's timing.
   */
  const paired = pairs.filter(
    pair => performedOnsetsMs[pair.sungIndex] !== undefined && referenceOnsetsMs[pair.referenceIndex] !== undefined
  )

  if (paired.length === 0) {
    /**
     * Nothing was paired, so there is nothing to say about timing - and that is not "together". A
     * caller has to be able to tell "no notes matched" from "every note was in place", so it is
     * `null`, the same shape `scorePhrase` uses for the same distinction.
     */
    return null
  }

  const centreMs = phraseCentreMs(
    paired.map(pair => performedOnsetsMs[pair.sungIndex] - referenceOnsetsMs[pair.referenceIndex])
  )

  const measured = paired.map(pair => {
    const reference = referenceOnsetsMs[pair.referenceIndex]
    const offset = referenceOffsetsMs[pair.referenceIndex] ?? 0
    const performed = performedOnsetsMs[pair.sungIndex] - centreMs
    const errorMs = performed - reference
    return {
      errorMs,
      band: bandOf(errorMs),
      referenceOffsetMs: offset,
      /*
       * The slot the reference note leans away from. Derived rather than passed, and it keeps any
       * global swing displacement IN the slot on purpose: swing is a constant per off-beat eighth,
       * so correlating it with per-note offsets would add a two-valued step function to both
       * series and report it as feel.
       */
      performedOffsetMs: performed - (reference - offset),
    }
  })

  /*
   * Displaced notes take no part in the feel match. It describes a LEAN - a small signed distance
   * from where a note belongs - and a note that landed on a different beat has no lean, it has a
   * position.
   */
  const leaning = measured.filter(entry => entry.band !== 'wrong-place')

  /*
   * The gate is on the WHOLE reference, not on the paired notes: §6.2 asks whether the melody has
   * a feel, which is a fact about the melody. Gating on the pairs would make the sentence appear
   * and disappear depending on which notes the player happened to hit.
   */
  const referenceLean = referenceOffsetsMs.reduce((widest, offset) => Math.max(widest, Math.abs(offset)), 0)
  const feelMatch =
    referenceLean >= feelMatchMinOffsetMs
      ? feelCorrelation(
          leaning.map(entry => entry.performedOffsetMs),
          leaning.map(entry => entry.referenceOffsetMs)
        )
      : undefined

  return {
    perNote: measured.map(entry => ({ errorMs: entry.errorMs, band: entry.band })),
    feelMatch,
  }
}
